<template>
  <div>
    <vs-alert
      :active="!protocolCategory && !loading"
      color="danger"
      icon="error_outline"
    >
      <span>Protocol Category ID: {{ protocolCategoryId }} not found!</span>
    </vs-alert>
    <vs-progress v-if="loading" indeterminate color="primary"></vs-progress>
    <div v-if="protocolCategory && !loading">
      <div id="main-container">
        <vx-card class="mb-5">
          <vs-row vs-justify="space-between" vs-align="center">
            <div>
              <h2>{{ protocolCategory.name_internal }} (ID: {{ protocolCategory.id }})</h2>
            </div>
            <div>
              <vs-button color="danger" type="border" class="mr-2" @click="confirmDelete" :disabled="loading">Delete</vs-button>
              <vs-button color="primary" @click="save" :disabled="loading">Save</vs-button>
            </div>
          </vs-row>
        </vx-card>

        <vs-alert
          :active="!!errors.length"
          color="danger"
          icon="error_outline"
          class="mb-5"
        >
          <div v-for="(error, index) in errors" :key="`pc_error_${index}`">{{ error }}</div>
        </vs-alert>

        <div class="vx-row">
          <div class="vx-col lg:w-1/2 w-full">
            <vx-card title="General" class="mb-5">
              <div class="vx-row mb-4">
                <div class="vx-col w-full">
                  <vs-input
                    class="w-full"
                    label="Name (Internal)"
                    v-model="workingCategory.name_internal"
                  />
                </div>
              </div>
            </vx-card>
          </div>
          <div class="vx-col lg:w-1/2 w-full mb-5">
            <vx-card title="Translations">
              <p class="text-sm mb-3 opacity-75">Clear a field and save to delete an existing translation.</p>
              <template v-for="(translation, index) in workingTranslations">
                <vs-input
                  class="w-full py-2 my-2"
                  :key="`language-${translation.language_id}`"
                  :label="translation.language_name"
                  v-model="workingTranslations[index].name"
                />
              </template>
            </vx-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/http/api";

export default {
  data() {
    return {
      loading: false,
      protocolCategory: null,
      protocolCategoryId: parseInt(this.$route.params.id),
      languages: [],
      workingCategory: { name_internal: '' },
      workingTranslations: [],
      errors: []
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;

      const [protocolCategory, languages] = await Promise.all([
        api.getProtocolCategory(this.protocolCategoryId),
        api.fetchAllLanguages()
      ]);

      this.protocolCategory = protocolCategory;
      this.languages = languages;

      if (this.protocolCategory) {
        this.buildWorkingCategory();
        this.buildWorkingTranslations();
      }

      this.loading = false;
    },
    buildWorkingCategory() {
      this.workingCategory = {
        name_internal: this.protocolCategory.name_internal || ''
      };
    },
    buildWorkingTranslations() {
      const existing = this.protocolCategory.translations || [];
      this.workingTranslations = this.languages.map((language) => {
        const match = existing.find(t => t.language_id === language.language_id);
        return {
          language_id: language.language_id,
          language_name: language.name,
          name: match ? match.name : '',
          original_name: match ? match.name : '',
          existed: !!match
        };
      });
    },
    async save() {
      this.errors = [];
      this.loading = true;

      await this.updateGeneral();
      await this.updateTranslations();
      await this.fetchData();

      if (!this.errors.length) {
        this.$vs.notify({
          title: 'Success',
          text: 'Saved Successfully!',
          color: 'success',
          icon: 'check_circle_outline'
        });
      }

      this.loading = false;
    },
    async updateGeneral() {
      const trimmed = (this.workingCategory.name_internal || '').trim();
      if (trimmed === (this.protocolCategory.name_internal || '')) return;
      if (!trimmed) {
        this.errors.push('Name (Internal) cannot be empty.');
        return;
      }
      const result = await api.updateProtocolCategory(this.protocolCategoryId, { name_internal: trimmed });
      if (!result) this.errors.push('The General section was NOT saved successfully.');
    },
    async updateTranslations() {
      const ops = this.workingTranslations.map((t) => {
        const trimmed = (t.name || '').trim();
        if (!t.existed && trimmed) {
          return api.createProtocolCategoryTranslation(this.protocolCategoryId, { language_id: t.language_id, name: trimmed });
        }
        if (t.existed && !trimmed) {
          return api.deleteProtocolCategoryTranslation(this.protocolCategoryId, t.language_id);
        }
        if (t.existed && trimmed !== t.original_name) {
          return api.updateProtocolCategoryTranslation(this.protocolCategoryId, t.language_id, { name: trimmed });
        }
        return Promise.resolve(true);
      });

      const results = await Promise.all(ops);
      if (results.some(r => r === false || r === null)) {
        this.errors.push('Some translations were NOT saved successfully.');
      }
    },
    confirmDelete() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Delete Protocol Category',
        text: `Are you sure you want to delete "${this.protocolCategory.name_internal}"? This cannot be undone.`,
        acceptText: 'Delete',
        cancelText: 'Cancel',
        accept: this.deleteCategory
      });
    },
    async deleteCategory() {
      this.loading = true;
      const ok = await api.deleteProtocolCategory(this.protocolCategoryId);
      this.loading = false;

      if (!ok) {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to delete protocol category.',
          color: 'danger',
          icon: 'error_outline'
        });
        return;
      }

      this.$vs.notify({
        title: 'Deleted',
        text: 'Protocol Category deleted.',
        color: 'success',
        icon: 'check_circle_outline'
      });
      this.$router.push('/content/protocol-categories').catch(() => {});
    }
  }
}
</script>